import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import type { InvitationValidationResponse } from '@zag/auth/models';

/** Payload for accepting an invitation via existing-account password flow. */
export interface AcceptInvitationExistingPayload {
  token: string;
  mode: 'existing';
  password: string;
}

/** Payload for accepting an invitation by creating a new account. */
export interface AcceptInvitationPasswordPayload {
  token: string;
  mode: 'password';
  name: string;
  password: string;
}

/** Payload for accepting an invitation after completing a social OAuth flow. */
export interface AcceptInvitationSocialPayload {
  token: string;
  mode: 'social';
}

export type AcceptInvitationPayload = AcceptInvitationExistingPayload | AcceptInvitationPasswordPayload | AcceptInvitationSocialPayload;

export interface AcceptInvitationResponse {
  email?: string;
}

/** Credentials for the better-auth email sign-in endpoint. */
export interface EmailSignInPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Service for the email-invitation acceptance flow.
 *
 * Wraps all HTTP calls made by the accept-invitation page:
 *  - validate the invitation token;
 *  - accept via password (new or existing account) / social callback;
 *  - complete the flow by signing the user in through better-auth.
 *
 * Consumers never see raw URLs, tokens-in-paths, or request shapes.
 */
@Injectable({ providedIn: 'root' })
export class InvitationAcceptanceService {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/api/v1/auth';

  /** Validate an invitation token and return the invitation metadata. */
  validate(token: string): Observable<InvitationValidationResponse> {
    return this.http.get<InvitationValidationResponse>(`${this.basePath}/invitations/${encodeURIComponent(token)}`);
  }

  /** Accept an invitation. Returns basic information about the invitee when provided. */
  accept(payload: AcceptInvitationPayload): Observable<AcceptInvitationResponse> {
    return this.http.post<AcceptInvitationResponse>(`${this.basePath}/invitation-acceptances`, payload);
  }

  /** Complete the flow by signing the user in via better-auth. */
  signInWithEmail(payload: EmailSignInPayload): Observable<unknown> {
    return this.http.post('/api/auth/sign-in/email', { rememberMe: false, ...payload });
  }
}
